// Text size + focus + autofill treatments for the Studio onboarding flow,
// matching Figma.
//
// Text size: 14px at every width. This deliberately overrides the theme's
// responsive input sizing (13px, bumped to 16px below 960px so iOS doesn't zoom
// the page on focus) — the funnel reads as one form instead of changing size at
// the breakpoint. Excludes the verification-code digits, which set their own.
//
// Input focus:
//  - Active (pointer/click): subtle darker border (#C9CBCD), no halo.
//  - Focused (keyboard Tab): neutral border + info-primary (#0C41BB / `link`)
//
// Autofill: Safari/Chrome paint `:-webkit-autofill` with a pale green/yellow
// wash. On the prefilled name field that reads as a "completed" fill against
// the Studio lime atmosphere. There is no intentional completed-field fill —
// force a white inset shadow so autofilled inputs match empty ones.
export const STUDIO_INPUT_CLASS = [
  '[&_.MuiOutlinedInput-input]:text-base',
  '[&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)_.MuiOutlinedInput-notchedOutline]:!border',
  '[&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)_.MuiOutlinedInput-notchedOutline]:!border-[#c9cbcd]',
  '[html[data-studio-keyboard]_&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)_.MuiOutlinedInput-notchedOutline]:!border-non-hover-border',
  '[html[data-studio-keyboard]_&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)]:!outline',
  '[html[data-studio-keyboard]_&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)]:!outline-[1.5px]',
  '[html[data-studio-keyboard]_&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)]:!outline-link',
  '[html[data-studio-keyboard]_&_.MuiOutlinedInput-root.Mui-focused:not(.Mui-error)]:!outline-offset-2',
  '[&_input:-webkit-autofill]:!shadow-[inset_0_0_0_1000px_#fff]',
  '[&_input:-webkit-autofill:hover]:!shadow-[inset_0_0_0_1000px_#fff]',
  '[&_input:-webkit-autofill:focus]:!shadow-[inset_0_0_0_1000px_#fff]',
  '[&_input:-webkit-autofill:active]:!shadow-[inset_0_0_0_1000px_#fff]',
  '[&_input:-webkit-autofill]:[-webkit-text-fill-color:theme(colors.black-heading)]',
  '[&_input:-webkit-autofill]:[caret-color:theme(colors.black-heading)]',
  // Browser transitions background-color onto autofill; delay it so the inset
  // shadow remains the visible fill.
  '[&_input:-webkit-autofill]:[transition:background-color_99999s_ease-in-out_0s]',
].join(' ');

// Buttons (Google secondary + studio primary) use a text-primary
// (#212B36 / `black-heading`) halo offset 1px outside the button.
export const STUDIO_BUTTON_FOCUS_CLASS = [
  'focus:!outline',
  'focus:!outline-[1.5px]',
  'focus:!outline-offset-1',
  'focus:!outline-black-heading',
].join(' ');

// Studio primary CTA button: the Figma label is 16px regular, but the
// design-system button renders its label as a nested medium 14px <p>, so the
// size/weight are overridden on that element. Includes the focus halo.
//
// The design-system `studio` variant renders its disabled state as
// `opacity-50` over the pale green fill with `text-disabled` (#C9CBCD) gray,
// which is too low-contrast to read. Override it (full opacity, deeper lime
// fill, mid-gray label) so the label — and the loading spinner, which inherits
// the label color via `currentColor` — stay legible.
export const STUDIO_PRIMARY_BUTTON_CLASS = [
  '[&_p]:font-normal',
  '[&_p]:text-lg',
  'disabled:!opacity-100',
  'disabled:!bg-studio-green-disabled',
  'disabled:!text-studio-disabled-text',
  STUDIO_BUTTON_FOCUS_CLASS,
].join(' ');
